import { useState } from 'react'

import { platform } from '@ctcells/core'

import type {
  ConsumableReplenishment,
  HistoryEntry,
  LogEntry,
  WasteReplacement,
} from '@ctcells/contract'
import { APP_LOGS, AUDIT_HISTORY, HISTORY } from '@ctcells/fixtures'
import { enumLabel, severityLabel, t, type Severity } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import type { TableSort } from '../../atoms/tableSort.ts'
import { FilterPill } from '../../atoms/FilterPill.tsx'
import { SearchInput } from '../../atoms/SearchInput.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { severityBadgeFlat } from '../../atoms/statusTone.ts'
import { Toolbar } from '../../atoms/Toolbar.tsx'
import { ListPage } from '../../layout/ListPage.tsx'
import { ADMIN_TABS, type AdminTabId } from '../admin/adminTabs.ts'
import { useGroupTabLayout } from '../groupTabs.ts'
import { Tabs, type TabItem } from '../../layout/Tabs.tsx'
import { viewGate, type ScreenAccess } from '../access.ts'
import { dateTimeText, errorMessageOf, historyTargetText, orDash } from '../format.ts'
import { OfflineNotice } from '../resourceParts.tsx'
import { logLevelRank, severityRank } from '../sortRanks.ts'
import { eventRowKey, ledgerOperation } from '../batches/eventColumns.tsx'
import { logsCsv, logsFileName } from './logsCsv.ts'

import {
  replenishmentColumns,
  replenishAfterText,
  wasteReplacementColumns,
} from './recordColumns.tsx'

import '../screens.css'
import '../resource.css'

/**
 * 로그 — 와이어프레임 `hist()` 2356행. 이벤트 · 시스템 · 감사 + 기록 둘(보충 · 폐기통 교체).
 *
 * 계약이 두 엔드포인트로 갈린다:
 *   이벤트 = `GET /history?event_type=event` + `…=batch_record`(한 목록 · 2026-10-08 배치 기록이 떨어져 나간 원장)
 *   감사 = `GET /history?event_type=audit`
 *   시스템 = `GET /logs`(`LogEntry`)
 *
 * 🔴 **시스템 · 감사 탭은 `settings` 또는 `maintenance` 가 있어야 보인다**(admin · service).
 *    그 권한이 없으면 탭이 하나뿐이라 **탭 줄 자체가 사라진다**(§1-3). `Tabs` 가 집행한다.
 * 🔴 **카운트 텍스트를 쓰지 않는다.** 「최근 200줄 · 자동 갱신」 같은 문구 금지(§1-1).
 * 🔴 **감사 탭 상단에 해시체인 검증 띠를 두지 않는다**(§1-4). 검증은 감사 행 하나로 남는다.
 */
export type LogTab = 'event' | 'syslog' | 'audit' | 'replenish' | 'waste'

export interface LogsScreenProps {
  /**
   * 열린 관리 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다(`admin/adminTabs.ts`).
   * **안 넘기면 무리 탭 줄을 안 그린다**(규칙은 `useGroupTabLayout` 주석) — 장비 관점 콘솔이
   * 그렇게 쓴다. 거기선 모듈·로그가 사이드바 「시스템」에 각자 서 있어서 탭 줄이 또 뜨면 안 된다.
   */
  adminTab?: AdminTabId | undefined
  events?: readonly HistoryEntry[]
  audits?: readonly HistoryEntry[]
  logs?: readonly LogEntry[]
  /**
   * 탭별로 **못 봤을 때의 사유**. 넣은 탭만 빈 문구가 이것으로 바뀐다.
   *
   * 🔴 **세 탭은 «각각» 다른 요청이다**(`/history?event_type=event` · `/logs` ·
   *    `/history?event_type=audit`). 하나가 실패했다고 셋을 같이 덮으면 **본 것까지 못 보게**
   *    된다 — 그래서 사유를 탭 단위로 나른다.
   * 🔴 **화면째 에러로 바꾸지 않는 이유는 «탭 줄»이다**(2026-09-21). 셋 다 operator/admin 이라
   *    viewer 는 전부 403 인데, 화면을 통째로 갈아 끼우면 **관리 탭 줄이 사라져** viewer 가
   *    모듈(서버가 200 을 주는 화면)로 갈 길을 잃는다.
   */
  notices?: {
    events?: string | undefined
    logs?: string | undefined
    audits?: string | undefined
    replenishments?: string | undefined
    wasteReplacements?: string | undefined
  }
  /**
   * 「기록」 두 탭(2026-10-12 · 백엔드 관리 화면 「기록」 묶음과 같은 둘) — 소모품 보충 이력(`GET /consumable-replenishments`) ·
   * 폐기통 교체 이력(`GET /waste-replacements`). 🔴 둘 다 **operator · admin** 이라 operator 에게도 탭이 선다
   * (시스템 · 감사는 admin · service 만). 안 넘기면(목 경로) 빈 표다 — 기록을 지어내지 않는다.
   */
  replenishments?: readonly ConsumableReplenishment[]
  wasteReplacements?: readonly WasteReplacement[]
  /** user_id → 이름(`/users` · admin 한정). 없거나 못 찾으면 사람 칸은 UUID 그대로다 */
  userNames?: ReadonlyMap<string, string> | undefined
  access?: ScreenAccess
  loading?: boolean
}

/**
 * 세 표의 처음 정렬 — 시각 최신이 위(서버가 주는 차례와 같다 · `history.py` `ts desc` · `views/logs.py` `time desc`).
 * 🔴 **머리글 정렬은 받은 줄(최근 `PAGE_LIMIT` 건 · `LogsContainer`) 안에서만이다.** 두 경로 모두 정렬 인자가 없다
 *    (`openapi.json` 실측 2026-09-30 — `limit · offset · event_type/level · batch_id/source · from · to`). 그래서
 *    「시각 오름차순」은 «받은 것 중 가장 오래된 것»이지 전체에서 가장 오래된 것이 아니다. 화면에 건수 문구를
 *    두지 않는 규칙(§1-1) 때문에 안내 줄은 안 붙였다 — 서버 정렬 인자 요청은 `docs/backend-requests.csv` BR-64.
 */
/** 목 이벤트 탭 — 「이벤트」 원장 둘(`event` · `batch_record`)을 한 목록으로(`LogsContainer` 의 실물 경로와 같은 모양) */
const DEMO_EVENT_LEDGERS: readonly HistoryEntry[] = HISTORY.filter(
  (entry) => entry.event_type === 'event' || entry.event_type === 'batch_record',
)

const LOG_DEFAULT_SORT: TableSort = { key: 'ts', direction: 'desc' }

/** 감사 행위 표기 — 칸과 정렬이 같은 글자를 쓴다 */
function auditActionText(entry: HistoryEntry): string {
  const action = detailText(entry, 'action')
  const label = action === undefined ? t('mask.none') : enumLabel('enum.auditAction', action)
  // 사전에 없는 행위는 원값을 흘리지 않고 동작 문구로 떨어뜨린다
  return label === t('mask.none') ? (detailText(entry, 'operation') ?? t('mask.none')) : label
}

/** `detail` 은 자유 객체다. 문자열 값만 안전하게 꺼낸다 */
function detailText(entry: HistoryEntry, key: string): string | undefined {
  const value = entry.detail?.[key]
  return typeof value === 'string' ? value : undefined
}

/**
 * 이벤트 한 줄의 **내용**. `HistoryEntry.detail` 에서 만든다.
 *
 * 🔴 **`detail.message` 를 읽지 마라.** 서버가 그 키를 만들지 않는다 — 이벤트 원장의 `detail` 은
 *    `category` · `operation` · `error_code` · `severity` **넷뿐**이다
 *    (`app/services/history.py:95-104` 의 `jsonb_build_object`). 2026-09-20 이전에는
 *    `message` 를 읽어 **실물에서 이 열이 «항상» 비었다**(fixture 에만 그 키가 있었다).
 *
 * 문구의 권위는 **에러 코드 사전**이다(`error_codes.yaml` ↔ `i18n` 의 `error.*`).
 * 화면이 문장을 만들지 않는다.
 * ⚠ 사전에 없는 코드는 **코드 원문**을 보인다 — 「처리하지 못했습니다」로 뭉개면 어느 코드인지
 *   알 수 없고, 사전에 없다는 사실 자체가 정보다.
 * ⚠ `operation` 은 자유 문자열(`run_centrifugation`)이라 번역하지 않는다. 코드 옆에 맥락으로 붙인다.
 */
function eventContentText(entry: HistoryEntry): string {
  const code = detailText(entry, 'error_code')
  // `batch_record` 행의 동작 낱말은 `kind` 다(`eventColumns.tsx` 머리말)
  const operation = ledgerOperation(entry)
  // 사전에 없으면 코드 원문이 나오도록 `message` 자리에 코드를 넘긴다
  const parts = [code === undefined ? undefined : errorMessageOf(code, code), operation]
  const text = parts.filter((part) => part !== undefined && part !== '').join(' · ')
  return text === '' ? t('mask.none') : text
}

export function LogsScreen({
  adminTab,
  events = DEMO_EVENT_LEDGERS,
  audits = AUDIT_HISTORY,
  logs = APP_LOGS,
  notices,
  replenishments = [],
  wasteReplacements = [],
  userNames,
  access = {},
  loading = false,
}: LogsScreenProps) {
  const [tab, setTab] = useState<LogTab>('event')
  const [query, setQuery] = useState('')
  const [exporting, setExporting] = useState(false)

  const permissions = access.permissions
  const privileged =
    (permissions?.includes('settings') ?? false) || (permissions?.includes('maintenance') ?? false)

  // 🔴 관리 무리의 탭 줄. 켜지면 위 줄은 관리 탭, 이 화면 자체 탭은 툴바 줄 왼쪽 맨 앞 2단 탭(`subnav` · 2026-10-13)이다 —
  //    두 줄이 같은 굵기면 위계가 안 읽힌다. 꺼지면 자체 탭이 위로 올라온다
  const groupTabs = useGroupTabLayout(ADMIN_TABS, adminTab, 'logs', access)

  // 기록 두 탭은 operator 도 본다(서버 `HISTORY_READ_ROLES = {operator, admin}`) — 이 화면 문이 `view` 라 viewer 는 여기 못 온다
  const recordTabs: TabItem<LogTab>[] = [
    { id: 'replenish', label: t('tab.logs.replenish') },
    { id: 'waste', label: t('tab.logs.waste') },
  ]
  const tabs: TabItem<LogTab>[] = privileged
    ? [
        { id: 'event', label: t('tab.logs.event') },
        { id: 'syslog', label: t('tab.logs.syslog') },
        { id: 'audit', label: t('tab.logs.audit') },
        ...recordTabs,
      ]
    : [{ id: 'event', label: t('tab.logs.event') }, ...recordTabs]

  const active = tabs.some((item) => item.id === tab) ? tab : 'event'
  /* 머리줄 탭 — 무리 탭이 켜져 있으면 관리 4탭, 꺼져 있으면 이 화면 자체 탭이다(운영 3종과 같은 모양) */
  const pageTabs: TabItem<string>[] = groupTabs ? groupTabs.tabs : tabs
  const pageTabValue: string = groupTabs ? groupTabs.value : active
  const onPageTabChange = (id: string): void => {
    if (groupTabs) groupTabs.onChange(id as AdminTabId)
    else setTab(id as LogTab)
  }

  const needle = query.trim().toLowerCase()
  const match = (...values: (string | null | undefined)[]) =>
    needle === '' || values.some((v) => typeof v === 'string' && v.toLowerCase().includes(needle))

  const eventRows = events.filter((e) =>
    match(e.target, detailText(e, 'message'), detailText(e, 'error_code')),
  )
  const auditRows = audits.filter((e) => match(e.actor, e.target, detailText(e, 'reason')))
  const logRows = logs.filter((l) => match(l.message, l.source))
  const replenishRows = replenishments.filter((r) =>
    match(r.module_id, r.note, replenishAfterText(r), userNames?.get(r.replenished_by ?? '')),
  )
  const wasteRows = wasteReplacements.filter((r) =>
    match(r.container_id, r.note, userNames?.get(r.replaced_by ?? '')),
  )

  const eventColumns: Column<HistoryEntry>[] = [
    {
      key: 'ts',
      header: t('column.time'),
      nowrap: true,
      sort: 'time',
      sortValue: (e) => e.ts,
      cell: (e) => <span className="muted mono">{dateTimeText(e.ts)}</span>,
    },
    {
      key: 'severity',
      header: t('column.severity'),
      nowrap: true,
      // 🔴 등급 순 — 칸이 그리는 대로 값 없음은 INFO 로 본다
      sort: (a, b) =>
        severityRank(detailText(a, 'severity') ?? 'INFO') -
        severityRank(detailText(b, 'severity') ?? 'INFO'),
      cell: (e) => {
        const severity = (detailText(e, 'severity') ?? 'INFO') as Severity
        // 바탕 있는 칩만 테를 걷는다 — 톤 없이 그려 오류 · 긴급(채움)만 flat · 경고 점선 · 정보는 테를 둔다(eric 2026-10-30 「칩 테두리를 만들어줘」)
        return (
          <StatusBadge
            flat={severityBadgeFlat(severity, undefined)}
            label={severityLabel(severity)}
            status={severity}
          />
        )
      },
      exportValue: (e) => severityLabel((detailText(e, 'severity') ?? 'INFO') as Severity),
    },
    {
      key: 'target',
      /*
       * 🔴 **「모듈」이 아니라 「대상」이다.** 서버가 배치 이벤트를 `batch:B-…` 로도 보낸다
       *    (`app/services/history.py:79-88`) — 모듈만 오는 자리가 아니다. 되돌리지 마라.
       * ⚠ 예전에는 `moduleLabelOf(e.target)` 를 그대로 불러 **`module:cent` 가 화면에 샜다**
       *   (2026-09-20 실측). 접두어를 떼는 것은 `historyTargetText()` 가 한다.
       */
      header: t('column.target'),
      nowrap: true,
      sort: 'text',
      sortValue: (e) => historyTargetText(e.target),
      cell: (e) => historyTargetText(e.target),
    },
    {
      key: 'content',
      header: t('column.content'),
      sort: 'text',
      sortValue: (e) => eventContentText(e),
      // 한 줄 고정. 전문은 툴팁으로 본다(§14-4)
      cell: (e) => {
        const text = eventContentText(e)
        return (
          <span className="nowrap" title={text}>
            {text}
          </span>
        )
      },
    },
    {
      key: 'kind',
      header: t('column.kind'),
      nowrap: true,
      sort: 'text',
      sortValue: (e) => enumLabel('enum.historyType', e.event_type),
      cell: (e) => enumLabel('enum.historyType', e.event_type),
    },
  ]

  const auditColumns: Column<HistoryEntry>[] = [
    {
      key: 'ts',
      header: t('column.time'),
      nowrap: true,
      sort: 'time',
      sortValue: (e) => e.ts,
      cell: (e) => <span className="muted mono">{dateTimeText(e.ts)}</span>,
    },
    {
      key: 'actor',
      header: t('column.actor'),
      nowrap: true,
      sort: 'text',
      sortValue: (e) => e.actor,
      cell: (e) => orDash(e.actor),
    },
    {
      key: 'action',
      header: t('column.action'),
      nowrap: true,
      sort: 'text',
      sortValue: (e) => auditActionText(e),
      cell: (e) => auditActionText(e),
    },
    {
      key: 'target',
      header: t('column.target'),
      nowrap: true,
      sort: 'text',
      sortValue: (e) => e.target,
      cell: (e) => orDash(e.target),
    },
    {
      key: 'reason',
      header: t('column.reasonChange'),
      sort: 'text',
      sortValue: (e) => detailText(e, 'reason'),
      cell: (e) => <span className="muted">{detailText(e, 'reason') ?? t('mask.none')}</span>,
    },
  ]

  const logColumns: Column<LogEntry>[] = [
    {
      key: 'ts',
      header: t('column.time'),
      nowrap: true,
      sort: 'time',
      sortValue: (l) => l.ts,
      cell: (l) => <span className="muted mono">{dateTimeText(l.ts)}</span>,
    },
    {
      key: 'level',
      header: t('column.logLevel'),
      nowrap: true,
      // 🔴 수준 순 — 글자 순이면 `critical < debug < error` 가 된다
      sort: (a, b) => logLevelRank(a.level) - logLevelRank(b.level),
      cell: (l) => l.level,
      exportValue: (l) => l.level,
    },
    {
      key: 'source',
      header: t('column.source'),
      nowrap: true,
      sort: 'text',
      sortValue: (l) => l.source,
      cell: (l) => <span className="mono">{orDash(l.source)}</span>,
    },
    {
      key: 'message',
      header: t('column.message'),
      sort: 'text',
      sortValue: (l) => l.message,
      cell: (l) => (
        <span className="nowrap" title={l.message}>
          {l.message}
        </span>
      ),
    },
  ]

  /*
   * 🔴 **「내보내기」 — 지금 탭의 표를 CSV 로**(eric 2026-10-14 「내보내기 버튼을 클릭하는데.. 왜 버튼이 안먹어?」 —
   *    `onClick` 없는 죽은 단추였다). 받은 행 · 검색으로 걸러진 행 그대로다(새 요청 없음 · `logsCsv.ts`).
   *    저장은 플랫폼 포트 뒤다(`platform.files.save` — 웹은 브라우저 내려받기).
   */
  const tabLabel = tabs.find((item) => item.id === active)?.label ?? t('screen.logs.title')
  const csvOf = (asOf: Date): { csv: string; count: number } => {
    const title = tabLabel
    const sort = LOG_DEFAULT_SORT
    switch (active) {
      case 'event':
        return {
          csv: logsCsv({ title, columns: eventColumns, rows: eventRows, sort, asOf }),
          count: eventRows.length,
        }
      case 'syslog':
        return {
          csv: logsCsv({ title, columns: logColumns, rows: logRows, sort, asOf }),
          count: logRows.length,
        }
      case 'audit':
        return {
          csv: logsCsv({ title, columns: auditColumns, rows: auditRows, sort, asOf }),
          count: auditRows.length,
        }
      case 'replenish':
        return {
          csv: logsCsv({
            title,
            columns: replenishmentColumns(userNames),
            rows: replenishRows,
            sort,
            asOf,
          }),
          count: replenishRows.length,
        }
      case 'waste':
        return {
          csv: logsCsv({
            title,
            columns: wasteReplacementColumns(userNames),
            rows: wasteRows,
            sort,
            asOf,
          }),
          count: wasteRows.length,
        }
    }
  }
  const activeCount =
    active === 'event'
      ? eventRows.length
      : active === 'syslog'
        ? logRows.length
        : active === 'audit'
          ? auditRows.length
          : active === 'replenish'
            ? replenishRows.length
            : wasteRows.length
  const exportPermission = viewGate(access, 'monitor')
  const exportGate = exportPermission.disabled
    ? exportPermission
    : loading
      ? ({ disabled: true, disabledReason: t('state.loading') } as const)
      : exporting
        ? ({ disabled: true, disabledReason: t('logs.export.saving') } as const)
        : activeCount === 0
          ? ({ disabled: true, disabledReason: t('logs.export.empty') } as const)
          : ({ disabled: false } as const)
  const exportCsv = async () => {
    setExporting(true)
    try {
      const asOf = new Date()
      await platform.files.save({
        fileName: logsFileName(tabLabel, asOf),
        mimeType: 'text/csv;charset=utf-8',
        data: csvOf(asOf).csv,
      })
    } finally {
      setExporting(false)
    }
  }

  return (
    <ListPage
      onTabChange={onPageTabChange}
      primaryAction={
        <Button
          onClick={() => {
            void exportCsv()
          }}
          size="pill"
          {...exportGate}
        >
          {exporting ? t('logs.export.saving') : t('action.export')}
        </Button>
      }
      tabValue={pageTabValue}
      tabs={pageTabs}
      tabsLabel={groupTabs ? t('screen.admin.title') : t('screen.logs.title')}
      title={groupTabs ? t('screen.admin.title') : t('screen.logs.title')}
      /*
       * 🔴 자체 탭(이벤트 · 시스템 · 감사 · 보충 기록 · 폐기통 교체)은 하위 메뉴다 — 서로 다른 목록 · 열 · 응답이라 필터가 아니다(eric 2026-10-13
       *    「검색창 왼쪽에 이렇게 놓은 건 보통 필터링할 때 목적으로 한 건데.. 지금은 필터링이 아니라 서브 메뉴잖아」 · 템플릿 종류와 같은 규칙).
       *    무리 탭이 켜지면(위 줄 = 관리 4탭) 툴바 줄 왼쪽 맨 앞 2단 탭(`subnav` · `ListPage` `subTabs`)으로 서고, 같은 줄 오른쪽은 진짜 필터
       *    (검색 · 기간 · 모듈 · 심각도 …)뿐이다. 무리 탭이 꺼지면 자체 탭이 머리줄로 올라가 이 줄은 없다(종전 그대로)
       */
      {...(groupTabs
        ? {
            subTabs: (
              <Tabs
                items={tabs}
                label={t('screen.logs.title')}
                onChange={setTab}
                value={active}
                variant="subnav"
              />
            ),
          }
        : {})}
      toolbar={
        <Toolbar label={t('screen.logs.title')}>
          <SearchInput
            onChange={setQuery}
            placeholder={
              active === 'audit'
                ? t('filter.search.audit')
                : active === 'syslog'
                  ? t('filter.search.syslog')
                  : active === 'replenish' || active === 'waste'
                    ? t('filter.search.records')
                    : t('filter.search.events')
            }
            value={query}
          />
          {/* 기록 두 탭에는 기간 칩을 세우지 않는다 — 칩이 아직 서버 인자로 안 흐른다(머리말) · 새 탭에 빈 자리를 늘리지 않는다 */}
          {active === 'replenish' || active === 'waste' ? null : (
            <FilterPill>{t('filter.period')}</FilterPill>
          )}
          {active === 'event' ? <FilterPill>{t('filter.module')}</FilterPill> : null}
          {active === 'event' ? <FilterPill>{t('filter.severity')}</FilterPill> : null}
          {active === 'syslog' ? <FilterPill>{t('filter.source')}</FilterPill> : null}
          {active === 'syslog' ? <FilterPill>{t('filter.logLevel')}</FilterPill> : null}
          {active === 'audit' ? <FilterPill>{t('filter.actor')}</FilterPill> : null}
          {active === 'audit' ? <FilterPill>{t('filter.kind')}</FilterPill> : null}
        </Toolbar>
      }
    >
      <OfflineNotice shown={access.offline === true} />

      {active === 'event' ? (
        <DataTable
          caption={t('tab.logs.event')}
          columns={eventColumns}
          defaultSort={LOG_DEFAULT_SORT}
          emphasis={(e) => detailText(e, 'category') === 'critical'}
          emptyLabel={t('state.empty.events')}
          loading={loading}
          {...(notices?.events === undefined ? {} : { notice: notices.events })}
          rowKey={eventRowKey}
          rows={eventRows}
        />
      ) : null}

      {active === 'syslog' ? (
        <DataTable
          caption={t('tab.logs.syslog')}
          columns={logColumns}
          defaultSort={LOG_DEFAULT_SORT}
          emphasis={(l) => l.level === 'error' || l.level === 'critical'}
          emptyLabel={t('state.empty.logs')}
          loading={loading}
          {...(notices?.logs === undefined ? {} : { notice: notices.logs })}
          rowKey={(l) => `${l.ts}-${l.message}`}
          rows={logRows}
        />
      ) : null}

      {active === 'audit' ? (
        <DataTable
          caption={t('tab.logs.audit')}
          columns={auditColumns}
          defaultSort={LOG_DEFAULT_SORT}
          emptyLabel={t('state.empty.audit')}
          loading={loading}
          {...(notices?.audits === undefined ? {} : { notice: notices.audits })}
          rowKey={(e) => `${e.ts}-${String(e.target)}`}
          rows={auditRows}
        />
      ) : null}

      {active === 'replenish' ? (
        <DataTable
          caption={t('tab.logs.replenish')}
          columns={replenishmentColumns(userNames)}
          defaultSort={LOG_DEFAULT_SORT}
          emptyLabel={t('state.empty.replenishments')}
          loading={loading}
          {...(notices?.replenishments === undefined ? {} : { notice: notices.replenishments })}
          rowKey={(r) => r.replenishment_id}
          rows={replenishRows}
        />
      ) : null}

      {active === 'waste' ? (
        <DataTable
          caption={t('tab.logs.waste')}
          columns={wasteReplacementColumns(userNames)}
          defaultSort={LOG_DEFAULT_SORT}
          emptyLabel={t('state.empty.wasteReplacements')}
          loading={loading}
          {...(notices?.wasteReplacements === undefined
            ? {}
            : { notice: notices.wasteReplacements })}
          rowKey={(r) => r.replacement_id}
          rows={wasteRows}
        />
      ) : null}
    </ListPage>
  )
}
